import { Grid, useTheme, Theme } from "@mui/material";
import { IUser } from "@vmosolutions/oc-interfaces";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { storeAppUsers } from "src/redux/actions/Structure.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { FlightErrorTable } from "./FlightErrorTable";
import { QualificationErrorTable } from "./QualificationErrorTable";
import { ViolationErrorTable } from "./ViolationErrorTable";
import { clearViolationsFromRedux } from "src/API/CrewScheduleErrors";

export enum ErrorType {
  NONE,
  FLIGHT_ERRORS,
  VIOLATION_ERRORS,
  QUALIFICATION_ERRORS,
}
interface IErrorsWindowProps {
  id: string;
  windowProps: {
    errorType: ErrorType;
  };
}

interface IErrorTypeOption {
  label: string;
  value: ErrorType;
}
const errorOptions: IErrorTypeOption[] = [
  { label: "Flight Errors", value: ErrorType.FLIGHT_ERRORS },
  { label: "Crew Violations", value: ErrorType.VIOLATION_ERRORS },
  { label: "Crew Qualification Errors", value: ErrorType.QUALIFICATION_ERRORS },
];

const ErrorWindowStyles = (theme: Theme): any => ({
  padding: "10px",
  "& .error_table_warning": {
    color: theme.custom.modal.warning,
  },
  "& .error_table": {
    margin: "5px",
    "& .optional_controls": {
      width: "100%",
      display: "flex",
      margin: "5px",
      "& .future_violations_checkbox": {
        width: "unset",
      },
    },
    "& .refresh_btn": {
      margin: "5px",
      padding: "2px 8px",
      height: "20px",
      fontSize: "10px",
      "& .refresh_icon": {
        width: "0.8em",
        height: "0.8em",
      },
    },
  },
});

export const ErrorsWindow = (props: IErrorsWindowProps): JSX.Element => {
  const { id, windowProps } = props;
  const [selectedErrorType, setSelectedErrorType] = useState<IErrorTypeOption>(null);
  const dispatch = useDispatch();

  const jumpToErrorType = windowProps?.errorType;

  useEffect(() => {
    if (jumpToErrorType) {
      const errorType = errorOptions.find((type) => type.value === jumpToErrorType);
      setSelectedErrorType(errorType);
    }
  }, [jumpToErrorType]);

  useEffect(() => {
    return () => {
      if (jumpToErrorType === ErrorType.VIOLATION_ERRORS) {
        //clear out the violations in redux
        dispatch(clearViolationsFromRedux());
      }
    };
  }, [jumpToErrorType, dispatch]);

  const appUsers = useSelector((state: IRootState) => state.structures.appUsers);
  useEffect(() => {
    const getUsersAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.GetPaginate<IUser>("/users");
      if (response) dispatch(storeAppUsers(response));
    };
    if (appUsers?.length === 0) getUsersAction();
  }, [dispatch, appUsers]);

  const errorTable = useMemo(() => {
    switch (selectedErrorType?.value ?? ErrorType.NONE) {
      case ErrorType.FLIGHT_ERRORS:
        return <FlightErrorTable id={id} />;
      case ErrorType.VIOLATION_ERRORS:
        return <ViolationErrorTable id={id} />;
      case ErrorType.QUALIFICATION_ERRORS:
        return <QualificationErrorTable id={id} />;
      case ErrorType.NONE:
        return null;
      default:
        return null;
    }
  }, [id, selectedErrorType]);

  return (
    <Grid container spacing={0}>
      <Grid item xs={12}>
        {errorTable}
      </Grid>
    </Grid>
  );
};
